import React, { useState, useEffect } from "react";
import "./style.css";

const Banners = ({ categoryId = null, maxBanners = 3 }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getApiBaseUrl = () => {
    return (
      process.env.REACT_APP_API_URL ||
      process.env.REACT_APP_BASE_URL ||
      "http://localhost:4000"
    );
  };

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) {

    }

    // If it's already a full URL, return as-is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    const baseUrl = getApiBaseUrl();
    let fullUrl;

    // Handle relative paths that start with /uploads/
    if (imagePath.startsWith("/uploads/")) {
      fullUrl = `${baseUrl}${imagePath}`;
    } 
    // Handle paths that start with uploads/ (no leading slash)
    else if (imagePath.startsWith("uploads/")) {
      fullUrl = `${baseUrl}/${imagePath}`;
    } 
    // Handle any path that starts with /
    else if (imagePath.startsWith("/")) {
      fullUrl = `${baseUrl}${imagePath}`;
    } 
    // Default: assume it's just a filename
    else {
      fullUrl = `${baseUrl}/uploads/banners/${imagePath}`;
    }


    return fullUrl;
  };

  const fetchBanners = async () => {
    try {
      setLoading(true);
      setError(null);

      const baseUrl = getApiBaseUrl();
      let apiUrl = `${baseUrl}/api/banners`;

      // Fix: categoryId should be catId based on your backend route
      if (categoryId) {
        apiUrl += `?catId=${categoryId}`;
      }



      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("token") && {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          }),
        },
      });



      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();


      let bannersData = [];

      // Handle different response formats
      if (data) {
        if (data.success === true && Array.isArray(data.data)) {
          bannersData = data.data;
        } else if (Array.isArray(data.data)) {
          bannersData = data.data;
        } else if (Array.isArray(data)) {
          bannersData = data;
        } else if (Array.isArray(data.banners)) {
          bannersData = data.banners;
        }
      }



      // Filter banners with valid images and log details
      const validBanners = bannersData.filter((banner, index) => {
        const hasImages = banner.images && banner.images.length > 0;

        return hasImages;
      });



      const limitedBanners = maxBanners
        ? validBanners.slice(0, maxBanners)
        : validBanners;

      setBanners(limitedBanners);

      
      // Log each banner's first image URL


    } catch (error) {
      console.error("❌ Error fetching banners:", error);
      setError(error.message || "Failed to load banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    fetchBanners();
  }, [categoryId, maxBanners]);

  // Loading state
  if (loading) {
    return (
      <div className="bannerSection">
        <div className="container-fluid">
          <div className="row g-3">
            {[...Array(maxBanners)].map((_, index) => (
              <div key={index} className="col-md-4">
                <div className="box">
                  <div
                    className="skeleton"
                    style={{
                      width: "100%",
                      height: "200px",
                      borderRadius: "15px",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bannerSection">
        <div className="container-fluid">
          <div className="row g-3">
            <div className="col-12">
              <div className="alert alert-danger text-center" role="alert">
                <h6 className="mb-2">❌ Unable to load banners</h6>
                <p className="mb-2">{error}</p>
                <small className="text-muted d-block mb-2">
                  API URL: {getApiBaseUrl()}/api/banners
                </small>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={fetchBanners}
                >
                  🔄 Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No banners state
  if (banners.length === 0) {
    return (
      <div className="bannerSection">
        <div className="container-fluid">
          <div className="row g-3">
            <div className="col-12">
              <div className="alert alert-warning text-center" role="alert">
                <h6 className="mb-2">📭 No banners available</h6>
                <p className="text-muted mb-2">
                  No banners found for the current criteria.
                </p>
                <small className="text-muted d-block mb-2">
                  Category ID: {categoryId || 'All'} | API: {getApiBaseUrl()}/api/banners
                </small>
                <button
                  className="btn btn-outline-success btn-sm"
                  onClick={fetchBanners}
                >
                  🔄 Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getColumnClass = (totalBanners) => {
    if (totalBanners === 1) return "col-md-12";
    if (totalBanners === 2) return "col-md-6";
    if (totalBanners >= 3) return "col-md-4";
    return "col-md-4";
  };

  const columnClass = getColumnClass(banners.length);

  return (
    <div className="bannerSection">
      <div className="container-fluid">
        <div className="row g-3">
          {banners.map((banner, index) => {
            const imageUrl = banner.images && banner.images[0]
              ? getFullImageUrl(banner.images[0])
              : null;

            return (
              <div
                key={banner._id || banner.id || index}
                className={columnClass}
              >
                <div className="box">
                  {imageUrl ? (
                    <>
                      <img
                        src={imageUrl}
                        className="banner-img w-100 transition"
                        alt={`${banner.catName || "Banner"} ${index + 1}`}
                        style={{
                          height: "200px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                        onError={(e) => {

                          e.target.style.display = "none";
                          const errorDiv = e.target.nextElementSibling;
                          if (errorDiv) {
                            errorDiv.style.display = "flex";
                          }
                        }}
                        onLoad={(e) => {

                        }}
                      />
                      {/* Error fallback */}
                      <div
                        className="banner-error-placeholder"
                        style={{
                          width: "100%",
                          height: "200px",
                          backgroundColor: "#ffebee",
                          display: "none",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "8px",
                          border: "1px solid #ffcdd2",
                          color: "#c62828",
                          flexDirection: "column",
                          padding: "10px",
                          textAlign: "center"
                        }}
                      >
                        <span>❌ Image Load Failed</span>
                        <small style={{ marginTop: "5px", wordBreak: "break-all" }}>
                          {imageUrl}
                        </small>
                      </div>
                    </>
                  ) : (
                    <div
                      className="banner-placeholder"
                      style={{
                        width: "100%",
                        height: "200px",
                        backgroundColor: "#f8f9fa",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        border: "1px solid #dee2e6",
                        color: "#6c757d",
                      }}
                    >
                      <span>📷 No Image Available</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default Banners;